import './InsightsPage.scss';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import React, { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Title from '../../shared/components/title/Title';
import { useTranslation } from 'react-i18next';
import { PROJECTS } from '../../shared/routes/path';
import FilterPanel from '../../shared/components/filter-panel/FilterPanel';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { useFilters } from '../../shared/contexts/filters/FiltersContext';
import useProjectNavigate from '../../shared/hooks/projects/preview/UseProjectNavigate';
import { useModuleAccess } from '../../shared/hooks/global/UseModuleAccess';
import { useGetProject } from '../../shared/api/hooks/project/useGetProject';
import { EffectiveFeaturesProvider } from '../../shared/contexts/effective-features/EffectiveFeaturesContext';
import { expandSelectedChatTypes } from '../../shared/helpers/MonitorTypeHelpers';
import { useGetMonitorsForProject } from '../../shared/api/hooks/monitor/useGetMonitorsForProject';
import PromptGroupDropdown from '../../shared/components/prompt-group-dropdown/PromptGroupDropdown';
import usePromptGroupId, { ALL_PROMPT_GROUPS_ID } from '../../shared/hooks/filters/UsePromptGroupId';
import { useGetInsightsOverview } from '../../shared/api/hooks/insights/useGetInsightsOverview';
import { useGetInsightsTimeSeries } from '../../shared/api/hooks/insights/useGetInsightsTimeSeries';
import { useGetInsightsPlatforms } from '../../shared/api/hooks/insights/useGetInsightsPlatforms';
import { useGetInsightsBrandFunnels } from '../../shared/api/hooks/insights/useGetInsightsBrandFunnels';
import { useGetInsightsBrandFunnelsTimeSeries } from '../../shared/api/hooks/insights/useGetInsightsBrandFunnelsTimeSeries';
import { useGetProjectAllBrandsStats } from '../../shared/api/hooks/project/stats/useGetProjectAllBrandsStats';
import OverviewKpis from './components/overview-kpis/OverviewKpis';
import GeneratedInsight from './components/generated-insight/GeneratedInsight';
import VisibilityCitedChart from './components/visibility-cited-chart/VisibilityCitedChart';
import PlatformGapBars from './components/platform-gap-bars/PlatformGapBars';
import CompetitorComparison from './components/competitor-comparison/CompetitorComparison';
import BrandFunnel from './components/brand-funnel/BrandFunnel';
import FunnelOverTimeChart from './components/funnel-over-time-chart/FunnelOverTimeChart';
import FunnelVsCompetitors from './components/funnel-vs-competitors/FunnelVsCompetitors';
import CompetitorFunnelOverTimeChart from './components/competitor-funnel-over-time-chart/CompetitorFunnelOverTimeChart';
import PromptPerformanceMatrix from './components/prompt-performance-matrix/PromptPerformanceMatrix';
import ChangeKeywordBox from '../../shared/components/change-keyword-box/ChangeKeywordBox';
import { useGetLiveBrandKeys } from '../../shared/api/hooks/project/stats/useGetLiveBrandKeys';
import { useGetTags } from '../../shared/api/hooks/tags/useGetTags';
import { useGetCommonKeywordsWithTags } from '../../shared/api/hooks/monitor/keyword/useGetCommonKeywordsWithTags';
import { findTagIdInKeyword, getBrandDisplayName, resolveBrandKey } from '../../shared/helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../shared/helpers/tags/TagsHelper';
import { GET_PROJECTS_LIST_KPIS } from '../../shared/api/query/project/stats/keys';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';

const InsightsPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { projectId } = useParams();
  const location = useLocation();

  const projectIdNumber = Number(projectId);
  const { accountResponse } = useAccount();
  const { selectedChatType, selectedChatTypes, range, monitorPeriod } = useFilters();
  const { project, refetch: reloadProject } = useGetProject(projectIdNumber);
  // Same gate as the sidebar entry: owner's INSIGHTS or super admin (dev-only preview).
  const insightsAccess = useModuleAccess('INSIGHTS', projectIdNumber || null, { allowSuperAdmin: true });
  const { promptGroupId } = usePromptGroupId();

  const [searchParams] = useSearchParams();
  const hasExplicitModelFilter = searchParams.get('models') !== null;
  const filterChatTypes = useMemo(() => {
    if (!hasExplicitModelFilter || !selectedChatTypes || selectedChatTypes.length === 0) {
      return undefined;
    }
    return Array.from(expandSelectedChatTypes(selectedChatTypes));
  }, [hasExplicitModelFilter, selectedChatTypes]);

  const { monitorPrompts } = useGetMonitorsForProject(projectIdNumber);

  const filteredMonitorPrompts = useMemo(() => {
    if (!monitorPrompts) return null;
    if (promptGroupId === ALL_PROMPT_GROUPS_ID) return monitorPrompts;
    return monitorPrompts.filter((p) => p.promptGroupId === promptGroupId);
  }, [monitorPrompts, promptGroupId]);

  const startDate = range?.[0] || null;
  const endDate = range?.[1] || null;

  const filteredMonitorPromptIds = useMemo(() => {
    if (!filteredMonitorPrompts || promptGroupId === ALL_PROMPT_GROUPS_ID) return undefined;
    return filteredMonitorPrompts.map((p) => p.id).filter((id): id is number => id != null);
  }, [filteredMonitorPrompts, promptGroupId]);

  const queryClient = useQueryClient();
  const { tags } = useGetTags(projectIdNumber);
  const { commonKeywords } = useGetCommonKeywordsWithTags(projectIdNumber);
  const { liveBrandKeys } = useGetLiveBrandKeys(
    projectIdNumber,
    filteredMonitorPrompts,
    selectedChatType,
    startDate,
    endDate,
    filterChatTypes
  );

  // The analyzed brand is the project's ChangeKeywordBox selection (firmName), exactly like the overview
  // page. The backend reads it from the project; it goes into every insights query key only so a switch
  // refetches. Label and own-brand flag resolve tags the same way the overview title does.
  const brand = project?.firmName ?? '';
  const hasBrandGrouping = insightsAccess.effectiveAddons.includes('KEYWORDS_PAGE');
  const brandLabel = useMemo(() => {
    if (!project || !brand) {
      return '';
    }
    const tagId = commonKeywords ? findTagIdInKeyword(brand, commonKeywords) : null;
    const brandName = getBrandDisplayName(brand, project.firmNameOrg, commonKeywords, hasBrandGrouping);
    return tags ? getTagLabel(tags, tagId, brandName) : brandName;
  }, [project, tags, commonKeywords, brand, hasBrandGrouping]);
  const isOwnBrand = useMemo(
    () =>
      !project ||
      !project.firmName ||
      resolveBrandKey(project.firmName, tags) === resolveBrandKey(project.defaultKeyword, tags),
    [project, tags]
  );

  const onBrandChanged = useCallback(async () => {
    // The ProjectsPage KPI columns are computed for the project's brand (firmName) — same as overview.
    queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KPIS] });
    await reloadProject();
  }, [queryClient, reloadProject]);

  const {
    insightsOverview,
    isLoading: isOverviewLoading,
    isError: isOverviewError,
  } = useGetInsightsOverview(
    selectedChatType
      ? {
          projectId: projectIdNumber,
          chatType: selectedChatType,
          from: startDate,
          to: endDate,
          monitorPromptIds: filteredMonitorPromptIds,
          filterChatTypes,
          brand,
        }
      : null
  );

  const {
    insightsTimeSeries,
    isLoading: isTimeSeriesLoading,
    isError: isTimeSeriesError,
  } = useGetInsightsTimeSeries(
    selectedChatType && monitorPeriod
      ? {
          projectId: projectIdNumber,
          chatType: selectedChatType,
          period: monitorPeriod,
          from: startDate,
          to: endDate,
          monitorPromptIds: filteredMonitorPromptIds,
          filterChatTypes,
          brand,
        }
      : null
  );

  const {
    insightsPlatforms,
    isLoading: isPlatformsLoading,
    isError: isPlatformsError,
  } = useGetInsightsPlatforms(
    selectedChatType
      ? {
          projectId: projectIdNumber,
          chatType: selectedChatType,
          from: startDate,
          to: endDate,
          monitorPromptIds: filteredMonitorPromptIds,
          filterChatTypes,
          brand,
        }
      : null
  );

  const {
    insightsBrandFunnels,
    isLoading: isBrandFunnelsLoading,
    isError: isBrandFunnelsError,
  } = useGetInsightsBrandFunnels(
    selectedChatType
      ? {
          projectId: projectIdNumber,
          chatType: selectedChatType,
          from: startDate,
          to: endDate,
          monitorPromptIds: filteredMonitorPromptIds,
          filterChatTypes,
          brand,
        }
      : null
  );

  const {
    insightsBrandFunnelsTimeSeries,
    isLoading: isBrandFunnelsTimeSeriesLoading,
    isError: isBrandFunnelsTimeSeriesError,
  } = useGetInsightsBrandFunnelsTimeSeries(
    selectedChatType && monitorPeriod
      ? {
          projectId: projectIdNumber,
          chatType: selectedChatType,
          period: monitorPeriod,
          from: startDate,
          to: endDate,
          monitorPromptIds: filteredMonitorPromptIds,
          filterChatTypes,
          brand,
        }
      : null
  );

  // SoV per marka jak na pie charcie overview (sovPerPrompt jak w V2) — zasila regułę widoczności
  // marek w selektorach "Funnel vs competitors" i macierzy promptów (top-15 pie + marka własna).
  const { allBrandsStatsResponse: sovBrandsStats, isLoading: isSovLoading } = useGetProjectAllBrandsStats(
    projectIdNumber,
    filteredMonitorPrompts,
    selectedChatType,
    monitorPeriod,
    startDate,
    endDate,
    filterChatTypes,
    true
  );

  useProjectNavigate(projectIdNumber);

  if (!accountResponse || !projectId || (!insightsAccess.isLoading && !insightsAccess.canAccess)) {
    return <Navigate to={PROJECTS} replace />;
  }

  // The same brand switcher as the overview page: it changes project.firmName and the backend scopes
  // every insights endpoint to that brand (own brand, competitor or tag).
  const filters = (
    <>
      {project && !project.demoVersion && (
        <ChangeKeywordBox project={project} changeKeyword={onBrandChanged} liveKeys={liveBrandKeys} />
      )}
      <FilterPanel baseUrl={location.pathname}>
        <PromptGroupDropdown projectId={projectIdNumber} />
      </FilterPanel>
    </>
  );

  return (
    <EffectiveFeaturesProvider
      features={project?.ownerFeatures ?? accountResponse.features ?? []}
      addons={insightsAccess.effectiveAddons}
    >
      <div className="insights-page page-content">
        <div className="page-box">
          {/* Telefon: tytuł w górnym pasku, wybór marki i filtry pod nim (jak na dashboardzie). */}
          <MobilePageHeader title={<span>{t('insightPage.header')}</span>} />
          {isMobile ? (
            <div className="mobile-page-filters">{filters}</div>
          ) : (
          <div className="page-box__filters" data-observe-sticky>
            <Title
              isBaseCategory={false}
              title={t('insightPage.header')}
              children={<div className="filters">{filters}</div>}
              showStatusDot={true}
              childrenOnMain={
                <div className="title-box__subtitle">{t('insightPage.desc', { brand: brandLabel })}</div>
              }
            />
          </div>
          )}
          <div className="page-box__stats">
            <div className="stats stats--full-width stats--padding-top">
              <OverviewKpis
                overview={insightsOverview}
                isLoading={isOverviewLoading}
                isError={isOverviewError}
                brandLabel={brandLabel}
                isOwnBrand={isOwnBrand}
              />
            </div>
            <div className="stats stats--full-width stats--padding-top">
              <GeneratedInsight
                overview={insightsOverview}
                isLoading={isOverviewLoading}
                brandLabel={brandLabel}
                isOwnBrand={isOwnBrand}
              />
            </div>
            <div className="stats stats--full-width">
              <VisibilityCitedChart
                buckets={insightsTimeSeries}
                period={monitorPeriod ?? 'DAILY'}
                isLoading={isTimeSeriesLoading}
                isError={isTimeSeriesError}
              />
            </div>
            <div className="stats stats--full-width">
              <PlatformGapBars
                platforms={insightsPlatforms}
                isLoading={isPlatformsLoading}
                isError={isPlatformsError}
              />
            </div>
            <div className="stats stats--full-width">
              <CompetitorComparison
                // Remount on project change — local brand selection and sort must not outlive the project.
                key={projectIdNumber}
                data={insightsBrandFunnels}
                sovBrands={sovBrandsStats}
                // Same dependencies and the same loader guard as FunnelVsCompetitors (see the comment there).
                isLoading={
                  isBrandFunnelsLoading || isSovLoading || filteredMonitorPrompts === null
                }
                isError={isBrandFunnelsError}
              />
            </div>
            <div className="stats stats--full-width">
              <BrandFunnel
                overview={insightsOverview}
                isLoading={isOverviewLoading}
                isError={isOverviewError}
              />
            </div>
            <div className="stats stats--full-width">
              {/* The funnel stages per time bucket — same time-series payload as VisibilityCitedChart
                  (the backend computes top3/top1 in that scan), so no extra request. */}
              <FunnelOverTimeChart
                buckets={insightsTimeSeries}
                period={monitorPeriod ?? 'DAILY'}
                isLoading={isTimeSeriesLoading}
                isError={isTimeSeriesError}
              />
            </div>
            <div className="stats stats--full-width">
              <FunnelVsCompetitors
                // Remount przy zmianie projektu — lokalny wybór marek nie może przeżyć projektu.
                key={projectIdNumber}
                data={insightsBrandFunnels}
                sovBrands={sovBrandsStats}
                // Sekcja czeka też na SoV: bez tego lista pickera i domyślny top-10 liczyłyby się
                // najpierw bez filtra SoV, a po dojściu danych skakały (loader ≠ "brak SoV").
                // `filteredMonitorPrompts === null` = monitory dopiero się ładują, a bez nich SoV
                // jeszcze nie wystartował (enabled wymaga promptów) i jego isLoading kłamie "false" —
                // bez tego twarde odświeżenie robi flash wykres→skeleton→wykres. Załadowana PUSTA
                // lista już tu nie wpada (SoV zostaje wyłączony → fallback bez filtra).
                isLoading={
                  isBrandFunnelsLoading || isSovLoading || filteredMonitorPrompts === null
                }
                isError={isBrandFunnelsError}
              />
            </div>
            <div className="stats stats--full-width">
              <CompetitorFunnelOverTimeChart
                // Remount on project change — like FunnelVsCompetitors (local brand selection and metric).
                key={projectIdNumber}
                buckets={insightsBrandFunnelsTimeSeries}
                period={monitorPeriod ?? 'DAILY'}
                brandFunnels={insightsBrandFunnels}
                sovBrands={sovBrandsStats}
                // Own series + the brand dictionary (brand-funnels) + the SoV picker guard of FunnelVsCompetitors.
                isLoading={
                  isBrandFunnelsTimeSeriesLoading ||
                  isBrandFunnelsLoading ||
                  isSovLoading ||
                  filteredMonitorPrompts === null
                }
                isError={isBrandFunnelsTimeSeriesError || isBrandFunnelsError}
              />
            </div>
            <div className="stats stats--full-width">
              <PromptPerformanceMatrix
                // Remount przy zmianie projektu — jak FunnelVsCompetitors (lokalny wybór marek
                // i strona paginacji). Fetch macierzy żyje W komponencie (stan paginacji steruje
                // zapytaniem), strona podaje tylko scope.
                key={projectIdNumber}
                scope={
                  selectedChatType
                    ? {
                        projectId: projectIdNumber,
                        chatType: selectedChatType,
                        from: startDate,
                        to: endDate,
                        monitorPromptIds: filteredMonitorPromptIds,
                        filterChatTypes,
                        brand,
                      }
                    : null
                }
                brandFunnels={insightsBrandFunnels}
                sovBrands={sovBrandsStats}
                // Zależności sekcji: słownik marek (brand-funnels) + filtr SoV — ten sam guard
                // co FunnelVsCompetitors.
                isAuxLoading={
                  isBrandFunnelsLoading || isSovLoading || filteredMonitorPrompts === null
                }
                isAuxError={isBrandFunnelsError}
              />
            </div>
          </div>
        </div>
      </div>
    </EffectiveFeaturesProvider>
  );
};

export default InsightsPage;
